/**
 * EventActionButton — one configured action button, as JSX.
 *
 * The Page Builder renderer builds these imperatively because the markup they
 * sit in is not React's. Here it is, so the same rules are expressed as
 * components: a redirect is a real <a href> (hover previews and middle-click
 * behave normally, and no JS is needed to navigate) and only a submit takes a
 * handler.
 *
 * The destination still comes from stored configuration via
 * resolveActionButtons — never from markup written on this page.
 */

import React from 'react';
import { completedMessage } from '../../../utils/actionCompletion';

const STYLE_CLASS = {
  filled: 'btn btn-primary',
  outlined: 'btn btn-outline-primary',
  text: 'btn btn-link',
};

/**
 * The inline result of an action.
 *
 * `role="status"` with `aria-live` so the outcome reaches a screen reader: the
 * message appears without any navigation, which is otherwise silent.
 */
export function EventActionFeedback({ state }) {
  if (!state) return null;

  const busy = state.status === 'busy';
  const failedWrite = state.status === 'error';
  const tone = failedWrite ? 'text-danger' : busy ? 'text-secondary' : 'text-success';

  return (
    <div
      className={`w-100 small mt-1 ${tone}`}
      role="status"
      aria-live="polite"
      data-arcee-action-state={state.status}
    >
      {busy ? 'Working…' : (state.message || (failedWrite ? 'Could not complete that.' : 'Done.'))}
    </div>
  );
}

/**
 * What stands in place of a button already completed on this record.
 *
 * Carries the same `data-arcee-action-state="completed"` hook the renderer
 * writes — eventDetail.css reads it to reveal "View my registrations".
 */
export function EventActionCompleted({ button }) {
  return (
    <div
      className="w-100 small text-success arcee-action-completed"
      role="status"
      data-arcee-action-state="completed"
      data-arcee-action-id={String(button.id)}
    >
      {completedMessage(button)}
    </div>
  );
}

export default function EventActionButton({ button, onAction }) {
  const className = STYLE_CLASS[button.style] || STYLE_CLASS.filled;
  const icon = button.icon ? <i className={`bi ${button.icon} me-1`} /> : null;

  if (button.type === 'redirect') {
    return (
      <a
        className={className}
        href={button.href}
        target={button.openInNewTab ? '_blank' : undefined}
        // Without noopener the opened page gets a handle back into this origin.
        rel={button.openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {icon}{button.label}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        if (onAction) onAction(button);
      }}
    >
      {icon}{button.label}
    </button>
  );
}
